/**
 * Symphenia – Datums- und Zeitformatierung
 *
 * Alle Formate richten sich nach den Betriebssystem-/Browser-Einstellungen
 * des Benutzers (navigator.language). Keine hardcodierten Locale-Strings.
 *
 * Verwendung:
 *   import { fmtDate, fmtTime, fmtDateLong, fmtDateGroupHeader } from '@/lib/format-date'
 */

/** Liefert die Locale des Browsers (= OS-Einstellung des Benutzers) */
export function userLocale(): string {
  return navigator.language ?? 'de-AT'
}

/**
 * Parsed einen Datums-String (ISO oder YYYY-MM-DD) sicher als lokales Datum,
 * ohne UTC-Verschiebung (vermeidet den "Tag um 1 verschoben"-Bug).
 */
export function parseLocalDate(dateStr: string): Date {
  // "2026-04-09" → als lokales Datum interpretieren, nicht als UTC
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  return new Date(dateStr)
}

/**
 * Kurzes Datum: "09.04.26" / "4/9/26" je nach OS-Einstellung
 */
export function fmtDate(date: Date | string, locale = userLocale()): string {
  const d = typeof date === 'string' ? parseLocalDate(date) : date
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: '2-digit' })
}

/**
 * Langes Datum mit Wochentag: "Do., 09.04.26" / "Thu, 4/9/26"
 */
export function fmtDateLong(date: Date | string, locale = userLocale()): string {
  const d = typeof date === 'string' ? parseLocalDate(date) : date
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' })
}

/**
 * Vollständiges Datum mit Wochentag: "Donnerstag, 09.04.2026" / "Thursday, 4/9/2026"
 */
export function fmtDateFull(date: Date | string, locale = userLocale()): string {
  const d = typeof date === 'string' ? parseLocalDate(date) : date
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString(locale, { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * Monats-Jahres-Anzeige: "April 2026"
 */
export function fmtMonthYear(date: Date | string, locale = userLocale()): string {
  const d = typeof date === 'string' ? parseLocalDate(date) : date
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
}

/**
 * Uhrzeit: "19:17" / "7:17 PM" je nach OS-Einstellung
 */
export function fmtTime(date: Date | string, locale = userLocale()): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
}

/**
 * Uhrzeit aus einem als UTC gespeicherten Wall-Clock-Timestamp: "13:27".
 *
 * Zeiteinträge werden im Backend als "Lokalzeit ohne Offset" abgelegt, d.h. die
 * vom User eingegebene Wall-Clock-Zeit landet 1:1 als UTC-Stempel in der DB
 * (z.B. 13:27 Vienna → "…T13:27:00.000Z"). Beim Anzeigen müssen daher die
 * UTC-Stunden/-Minuten gelesen werden, NICHT die lokalen — sonst entsteht in
 * UTC+x (z.B. CEST) eine Verschiebung um den Offset. 24h-Format, passend auch
 * direkt für input[type=time]. Konsistent mit der PWA (formatEntryTime).
 */
export function fmtTimeUTC(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
}

/**
 * Datum + Uhrzeit: "09.04.26, 19:17"
 */
export function fmtDateTime(date: Date | string, locale = userLocale()): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''
  return d.toLocaleString(locale, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

/**
 * Gruppen-Kopfzeile mit "Heute" / "Gestern" Kurzform:
 * "Heute, Donnerstag, 09.04.2026"
 */
export function fmtDateGroupHeader(dateStr: string, locale = userLocale()): string {
  const date = parseLocalDate(dateStr)
  if (isNaN(date.getTime())) return dateStr
  const today = new Date()
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const yStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`

  const full = fmtDateFull(date, locale)
  if (dateStr === todayStr)  return `Heute, ${full}`
  if (dateStr === yStr)      return `Gestern, ${full}`
  return full
}

/**
 * Wochenbereich für KW-Anzeige: "KW 15 · 06.04. – 12.04.2026"
 */
export function fmtWeekRange(from: Date, to: Date, kw: number, locale = userLocale()): string {
  const fromStr = from.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })
  const toStr   = to.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
  return `KW ${kw} · ${fromStr} – ${toStr}`
}

/**
 * ISO-Datumsstring (YYYY-MM-DD) aus einem Date-Objekt — für API-Calls,
 * unabhängig von Locale.
 */
export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
